// S7 Cost runs — list (/catalogue/cost-runs). Module 01 §4 / §3.7, on the
// revision-6 register.
//
// The ENTIRE route is cost-gated: catalogue.cost.read is enforced server-side,
// so a role without it receives 403 from GET /cost-runs and this screen renders
// an explicit "not available" state — the cost surface is unreachable/empty for
// that role, never a broken table (§7). Creating a run additionally needs
// catalogue.cost.run.
//
// One page-head, one primary action ("New cost run"), one Card holding the
// §3.3 DataTable. The create form opens as a modal so the screen never shows
// two competing primaries; it keeps its `Scope` / `As-of date` labels and its
// "Start roll-up" submit, which are unit-test contracts.

import { formatTimestampDate } from "../../../../lib/datetime";
import { type SyntheticEvent, useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { Card } from "../../../../components/card";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Banner } from "../../../../components/toast";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { useCostRuns, useCreateCostRun, useFacilities } from "../hooks/catalogue-queries";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { useCataloguePermissions } from "../hooks/permissions";
import { EmptyState, ErrorState } from "../../../../components/states";
import { StatusBadge } from "../../../../components/status-badge";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { UnauthorizedError } from "../../../../lib/problem";
import "../catalogue.css";

type CostRun = components["schemas"]["CostRun"];
type Scope = CostRun["scope"];

function isForbidden(error: unknown): boolean {
  return error instanceof RequestError && error.httpStatus === 403;
}

export function CostRunsScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const runs = useCostRuns();
  const perms = useCataloguePermissions();
  const canRun = perms.has("catalogue.cost.run");

  useEffect(() => {
    if (runs.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [runs.error, navigate]);

  // Route guard (§4 S7 "entire route guarded by catalogue.cost.read"): a role
  // without cost.read never reaches the cost surface. The server 403 (below) is
  // the backstop for roles the client cannot map (custom roles).
  const forbidden =
    (perms.isResolved && !perms.has("catalogue.cost.read")) || isForbidden(runs.error);

  const head = (
    <PageHeader
      title={t("catalogue.costRun.listTitle")}
      crumbs={[{ label: t("nav.catalogue"), to: "/catalogue/products" }]}
      actions={
        canRun && !forbidden ? (
          <Button
            variant="primary"
            data-testid="new-cost-run"
            onClick={() => {
              setShowForm(true);
            }}
          >
            {t("catalogue.costRun.newRun")}
          </Button>
        ) : undefined
      }
    />
  );

  if (forbidden) {
    return (
      <section className="cat-screen">
        {head}
        <EmptyState
          title={t("catalogue.costRun.forbiddenTitle")}
          body={t("catalogue.costRun.forbiddenBody")}
        />
      </section>
    );
  }

  const items = runs.data?.items ?? [];
  const failed = runs.error !== null && !(runs.error instanceof UnauthorizedError);

  const columns: DataTableColumn<CostRun>[] = [
    {
      key: "as_of_date",
      header: t("catalogue.costRun.colAsOf"),
      id: true,
      render: (run) => (
        <button
          type="button"
          className="ds-lnk"
          onClick={() => {
            void navigate({ to: "/catalogue/cost-runs/$id", params: { id: run.id } });
          }}
        >
          {run.as_of_date}
        </button>
      ),
    },
    {
      key: "scope",
      header: t("catalogue.costRun.colScope"),
      render: (run) => t(`catalogue.costRun.scope.${run.scope}`),
    },
    {
      key: "facility",
      header: t("catalogue.costRun.colFacility"),
      render: (run) => run.facility_id ?? t("catalogue.costRun.groupWide"),
    },
    {
      key: "status",
      header: t("catalogue.costRun.colStatus"),
      render: (run) => <StatusBadge status={run.status} />,
    },
    { key: "currency", header: t("catalogue.costRun.colCurrency") },
    {
      key: "created",
      header: t("catalogue.costRun.colCreated"),
      age: true,
      render: (run) => formatTimestampDate(run.created_at, i18n.language),
    },
  ];

  return (
    <section className="cat-screen">
      {head}

      <Card title={t("catalogue.costRun.listTitle")} count={items.length}>
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(run) => run.id}
          rowTestId="cost-run-row"
          caption={t("catalogue.costRun.listTitle")}
          loading={runs.isPending || runs.error instanceof UnauthorizedError}
          loadingLabel={t("catalogue.costRun.loadingList")}
          error={
            failed ? (
              <ErrorState
                title={t("catalogue.costRun.listErrorTitle")}
                detail={runs.error instanceof Error ? runs.error.message : undefined}
                retryLabel={t("catalogue.state.retry")}
                onRetry={() => void runs.refetch()}
                requestId={requestIdOf(runs.error)}
                requestIdLabel={t("catalogue.state.requestId")}
              />
            ) : undefined
          }
          empty={
            <EmptyState
              title={t("catalogue.costRun.listEmptyTitle")}
              body={t("catalogue.costRun.listEmptyBody")}
              ctaLabel={canRun ? t("catalogue.costRun.newRun") : undefined}
              onCta={
                canRun
                  ? () => {
                      setShowForm(true);
                    }
                  : undefined
              }
            />
          }
        />
      </Card>

      {showForm ? (
        <CreateCostRunForm
          onCancel={() => {
            setShowForm(false);
          }}
          onCreated={(id) => void navigate({ to: "/catalogue/cost-runs/$id", params: { id } })}
        />
      ) : null}
    </section>
  );
}

function CreateCostRunForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateCostRun();

  const [scope, setScope] = useState<Scope>("all_make_products");
  const [facilityId, setFacilityId] = useState("");
  // The DEFAULT as-of date is the FACILITY's today, not the viewer's — and not
  // UTC's, which the old `toISOString().slice(0, 10)` silently was (D-089:
  // data selection is facility-local; module 01 §3.7 resolves versions
  // effective ON this business date). It stays a DEFAULT — the picker's answer
  // wins from the first touch — so it is derived, not captured at mount (the
  // facilities read has not answered yet at mount), and it follows the typed
  // facility while the user has not picked a date: a facility-scoped run rolls
  // up in that facility's calendar; a group-wide run takes the first active
  // facility's. Fallback (no facilities yet / unknown id) is the browser
  // calendar, per `facilityTodayIso`'s documented degradation.
  const facilities = useFacilities();
  const typedFacilityId = facilityId.trim();
  const calendarFacility =
    facilities.data?.items.find((f) => f.id === typedFacilityId) ?? facilities.data?.items[0];
  const [pickedAsOf, setPickedAsOf] = useState<string | undefined>(undefined);
  const asOf = pickedAsOf ?? facilityTodayIso(Date.now(), calendarFacility?.timezone);
  const [note, setNote] = useState("");
  const [productIdsText, setProductIdsText] = useState("");
  const [clientError, setClientError] = useState<string | undefined>(undefined);

  function parseProductIds(): string[] {
    return productIdsText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    setClientError(undefined);

    if (asOf.length === 0) {
      setClientError(t("catalogue.costRun.errorAsOfRequired"));
      return;
    }
    const productIds = parseProductIds();
    if (scope === "selected_products" && productIds.length === 0) {
      setClientError(t("catalogue.costRun.errorProductIdsRequired"));
      return;
    }

    const facility = facilityId.trim();
    const trimmedNote = note.trim();
    const body: components["schemas"]["CostRunCreateRequest"] = {
      as_of_date: asOf,
      scope,
      ...(facility.length > 0 ? { facility_id: facility } : {}),
      ...(trimmedNote.length > 0 ? { note: trimmedNote } : {}),
      ...(scope === "selected_products" ? { product_ids: productIds } : {}),
    };

    create.mutate(body, {
      onSuccess: (run) => {
        onCreated(run.id);
      },
    });
  }

  const serverError = create.error instanceof RequestError ? create.error : undefined;

  return (
    <div className="cat-scrim">
      <form
        className="cat-modal"
        onSubmit={submit}
        data-testid="create-cost-run-form"
        aria-label={t("catalogue.costRun.createTitle")}
      >
        <div className="cat-modal-head">
          <h2>{t("catalogue.costRun.createTitle")}</h2>
        </div>

        <div className="cat-modal-body">
          <div className="cat-form">
            <div className="cat-fld">
              <label htmlFor="cost-run-as-of">{t("catalogue.costRun.fieldAsOf")}</label>
              <input
                id="cost-run-as-of"
                type="date"
                value={asOf}
                onChange={(e) => {
                  setPickedAsOf(e.target.value);
                }}
                required
              />
            </div>

            <div className="cat-fld">
              <label htmlFor="cost-run-scope">{t("catalogue.costRun.fieldScope")}</label>
              <select
                id="cost-run-scope"
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as Scope);
                }}
              >
                <option value="all_make_products">
                  {t("catalogue.costRun.scope.all_make_products")}
                </option>
                <option value="selected_products">
                  {t("catalogue.costRun.scope.selected_products")}
                </option>
              </select>
            </div>

            <div className="cat-fld">
              <label htmlFor="cost-run-facility">{t("catalogue.costRun.fieldFacility")}</label>
              <input
                id="cost-run-facility"
                type="text"
                value={facilityId}
                onChange={(e) => {
                  setFacilityId(e.target.value);
                }}
              />
              <span className="cat-hint">{t("catalogue.costRun.facilityHint")}</span>
            </div>

            <div className="cat-fld">
              <label htmlFor="cost-run-note">{t("catalogue.costRun.fieldNote")}</label>
              <input
                id="cost-run-note"
                type="text"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
              />
            </div>

            {scope === "selected_products" ? (
              <div className="cat-fld cat-form-wide">
                <label htmlFor="cost-run-product-ids">
                  {t("catalogue.costRun.fieldProductIds")}
                </label>
                <textarea
                  id="cost-run-product-ids"
                  rows={4}
                  value={productIdsText}
                  onChange={(e) => {
                    setProductIdsText(e.target.value);
                  }}
                  placeholder={t("catalogue.costRun.productIdsHint")}
                />
                <span className="cat-hint">{t("catalogue.costRun.productIdsHint")}</span>
              </div>
            ) : null}
          </div>

          {clientError !== undefined ? <Banner kind="bad">{clientError}</Banner> : null}
          {serverError !== undefined ? (
            <Banner kind="bad">
              {t("catalogue.costRun.createError")}
              {serverError.requestId !== undefined ? (
                <span className="state-request-id">
                  {" "}
                  {t("catalogue.state.requestId")}: <code>{serverError.requestId}</code>
                </span>
              ) : null}
            </Banner>
          ) : null}
        </div>

        <div className="cat-modal-foot">
          <div className="cat-modal-foot-right">
            <Button type="button" onClick={onCancel} disabled={create.isPending}>
              {t("catalogue.costRun.cancel")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              busy={create.isPending}
              busyLabel={t("catalogue.costRun.creating")}
            >
              {t("catalogue.costRun.create")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
